import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "../components/Nav";
import ProfileCard from "../components/ProfileCard";
import VibeTag from "../components/VibeTag";
import ProgressionIndicator from "../components/ProgressionIndicator";
import { getToken } from "../lib/auth";

export default function SelectSeven() {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load real prospects from API (scored if authenticated)
  useEffect(() => {
      const token = getToken();
      if (!token) { navigate('/login'); return; }
      const headers = { Authorization: `Bearer ${token}` };
      fetch('/api/matches/eligible', { headers })
      .then((r) => {
        if (r.status === 403) {
          navigate('/discovery');
          return null;
        }
        return r.ok ? r.json() : null;
      })
      .then((data) => { setProspects(data?.matches || []); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  // Rehydrate saved shortlist on revisit
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch('/api/shortlist', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data?.prospect_ids?.length) setPicks(data.prospect_ids); })
      .catch(() => {});
  }, []);

  const toggle = (id) => {
    setPicks((s) =>
      s.includes(id)
        ? s.filter((x) => x !== id)
        : s.length < 7
          ? [...s, id]
          : s
    );
  };

  const slotCount = 7;
  const slotsFilled = picks.length;

  
const eligibleCount = prospects?.length || 0;

if (loading) return (
  <AppShell>
    <div style={{ textAlign: 'center', paddingTop: '40vh', color: '#aaa' }}>
      Loading matches…
    </div>
  </AppShell>
);

if (eligibleCount < 7) return (
  <AppShell>
    <div style={{ textAlign: 'center', paddingTop: '35vh' }}>
      <div style={{ color: '#F2DE94', fontSize: '1.1rem', marginBottom: '12px' }}>
        We're gathering people who match your criteria. Check back soon.
      </div>
      <div style={{ color: '#888', fontSize: '0.85rem' }}>
        You'll be able to choose your seven as soon as enough eligible matches are available.
      </div>
    </div>
  </AppShell>
);

return (
    <AppShell>
      <div className="px-6 md:px-12 py-10 md:py-14">
        {/* Heading */}

        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <span className="glass rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.24em] text-[#F2DE94]">
            Round 03 · Chemistry
          </span>
          <span className="glass rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-white/70">
            Shortlist in motion
          </span>
          <span className="text-xs text-white/45">5 advancing · 2 paths closed</span>
        </div>
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
              Signature · the draft
            </div>
            <h1 className="font-display text-4xl md:text-6xl leading-[1.02]">
              Select <span className="italic text-gradient-gold">your seven.</span>
            </h1>
            <p className="text-white/60 mt-3 max-w-xl">
              A private shortlist you'll guide through the rounds. Choose those
              you're most curious about — not just the ones who look loudest.
            </p>
          </div>
          <div className="flex items-center gap-4 glass rounded-2xl px-5 py-4">
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">Draft</div>
              <div className="font-display text-3xl">
                {slotsFilled}
                <span className="text-white/35"> / {slotCount}</span>
              </div>
            </div>
            <div className="h-10 w-px bg-white/10" />
            <div className="w-40">
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50 mb-1">Progress</div>
              <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                <div
                  className="h-full bg-gradient-gold transition-[width] duration-500"
                  style={{ width: `${(slotsFilled / slotCount) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Progression */}
        <div className="mb-8">
          <ProgressionIndicator variant="clear" current={0} showCaption={false} />
        </div>

        {/* The 7 seats */}
        <div className="mb-12 glass-strong rounded-3xl p-5 md:p-7">
          <div className="flex items-baseline justify-between mb-4">
            <div className="font-display text-xl">Your shortlist</div>
            <div className="text-xs text-white/45">
              Drag to reorder · Tap to preview
            </div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-7 gap-3">
            {Array.from({ length: 7 }).map((_, i) => {
              const pid = picks[i];
              const p = pid ? prospects.find((x) => x.id === pid) : null;
              if (!p) {
                return (
                  <div
                    key={i}
                    data-testid={`empty-seat-${i + 1}`}
                    className="relative rounded-2xl border border-dashed border-white/10 aspect-[3/4] grid place-items-center text-white/35"
                  >
                    <div className="text-center">
                      <div className="text-3xl font-display">{i + 1}</div>
                      <div className="text-[10px] uppercase tracking-[0.22em] mt-1">Open seat</div>
                    </div>
                  </div>
                );
              }
              const videoSrc = p.video_url || p.video || p.intro_video_url;
              return (
                <div key={p.id} className="relative rounded-2xl overflow-hidden aspect-[3/4] ring-gold group">
                  <img src={p.image} alt={p.name} className="absolute inset-0 h-full w-full object-cover" />
                  {videoSrc && (
                    <video
                      ref={(el) => { if (el) el.muted = true; }}
                      src={videoSrc}
                      className="absolute inset-0 w-full h-full object-cover z-10"
                      autoPlay
                      loop
                      playsInline
                      preload="auto"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-transparent z-20" />
                  <div className="absolute top-2 left-2 h-6 w-6 rounded-full bg-gradient-gold grid place-items-center text-[10px] font-semibold text-[#05070d] z-30">
                    {i + 1}
                  </div>
                  <button
                    onClick={() => toggle(p.id)}
                    data-testid={`remove-pick-${p.id}`}
                    className="absolute top-2 right-2 h-6 w-6 rounded-full glass grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity text-white/80 z-30"
                  >
                    ✕
                  </button>
                  <div className="absolute bottom-2 inset-x-2 z-30">
                    <div className="font-display text-sm truncate">{p.name}</div>
                    <div className="text-[10px] text-[#F2DE94]">{p.compatibility ?? ''}{ p.tags?.[0] ? ` · ${p.tags[0]}` : ''}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <div className="text-xs text-white/45">
              {7 - slotsFilled === 0
                ? "Your seven is set. You can replace anyone before Round 1."
                : `${7 - slotsFilled} seat${7 - slotsFilled === 1 ? "" : "s"} still open.`}
            </div>
            <button
              onClick={() => {
                if (slotsFilled !== 7) return;
                const token = getToken();
                if (token) {
                  fetch('/api/shortlist', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ prospect_ids: picks }),
                  }).catch(() => {});
                }
                navigate('/soft-transition');
              }}
              data-testid="lock-draft"
              className={`rounded-full font-semibold px-6 py-3 transition-transform ${
                slotsFilled === 7
                  ? "bg-gradient-gold text-[#080B14] glow-gold hover:scale-[1.02]"
                  : "bg-white/5 text-white/40 cursor-not-allowed"
              }`}
            >
              Lock draft & begin Round 1 →
            </button>
          </div>
        </div>

        {/* Pool */}
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="font-display text-2xl">The pool</h2>
          <div className="text-xs text-white/45">Tap to shortlist</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {prospects.map((p) => (
            <ProfileCard
              key={p.id}
              p={p}
              selectable
              state={picks.includes(p.id) ? "selected" : "idle"}
              onSelect={() => toggle(p.id)}
            />
          ))}
        </div>
      </div>
    </AppShell>
  );
}